import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { type ContactPayload, Contacts } from '@capacitor-community/contacts';
import { isSupportedCountry, parsePhoneNumber } from 'react-phone-number-input';

import { toast } from '@/utils/toast';

/** The phone's address book is a native-only source; the web keeps typing by hand. */
export const canPickDeviceContact = (): boolean => Capacitor.isNativePlatform();

export interface DeviceContact {
  name: string;
  /** An email when the contact has one, else a phone in E.164 where it parses; '' when neither. */
  contact: string;
}

/** `en-US` → `US`: the device region a number without a country code is read in. */
const deviceRegion = async () => {
  const { value } = await Device.getLanguageTag().catch(() => ({ value: '' }));
  const region = value.split('-').pop()?.toUpperCase() ?? '';
  return isSupportedCountry(region) ? region : 'US';
};

/** An unparsable number is kept as entered, so the form's phone validation flags it. */
const toE164 = async (raw: string) => {
  const parsed = parsePhoneNumber(raw, await deviceRegion());
  return parsed?.number ?? raw.trim();
};

const nameOf = ({ name }: ContactPayload) =>
  (name?.display ?? [name?.given, name?.family].filter(Boolean).join(' ')).trim();

/**
 * Opens the OS contact picker and reads the one contact chosen. The first email wins,
 * then the first phone. Resolves `null` on an error the toast has already explained;
 * on cancel the plugin never settles, which leaves the form exactly as it was.
 */
export const pickDeviceContact = async (): Promise<DeviceContact | null> => {
  try {
    const { contact } = await Contacts.pickContact({
      projection: { name: true, emails: true, phones: true },
    });
    const email = contact.emails?.find((item) => item.address?.trim())?.address?.trim();
    const phone = contact.phones?.find((item) => item.number?.trim())?.number;
    return {
      name: nameOf(contact),
      contact: email ?? (phone ? await toE164(phone) : ''),
    };
  } catch (error) {
    const denied = error instanceof Error && /permission/i.test(error.message);
    toast.error(
      denied
        ? 'Couldn’t open contacts. Please allow access in Settings.'
        : 'Couldn’t read this contact. Please try again.',
    );
    return null;
  }
};
